import './MemoryMonitor.scss';
import { Component } from '@components/Component/Component.js';
import { createElement } from '@components/Utility/index.js';
// A simple in-app memory monitor using the non-standard performance.memory API.
// works in chrome only
export class MemoryMonitor extends Component {
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'MemoryMonitor';
        this.data = [];
        this.maxDataPoints = 100;
        this.canvasWidth = 100;
        this.canvasHeight = 60;
        this.lastUsedHeap = 0;
        this.gcHistory = [];
        this.frameId = null;
        this.rootElement = rootElement || createElement('div');
        this.hide();
    }
    draw() {
        super.draw();
        const ctx = this.canvas.getContext('2d');
        if (!ctx) {
            throw new Error('MemoryMonitor: Unable to get 2D context');
        }
        this.ctx = ctx;
        document.body.appendChild(this.rootElement);
    }
    /**
     * Starts the monitoring loop.
     */
    start() {
        if (!this.drawn) {
            this.draw();
        }
        this.show();
        // Avoid multiple loops
        if (this.frameId !== null) {
            return;
        }
        const loop = () => {
            this.update();
            this.frameId = requestAnimationFrame(loop);
        };
        this.frameId = requestAnimationFrame(loop);
    }
    /**
     * Stops the monitoring loop and optionally removes the canvas.
     */
    stop(removeCanvas = false) {
        this.hide();
        if (this.frameId !== null) {
            cancelAnimationFrame(this.frameId);
            this.frameId = null;
        }
        if (removeCanvas && this.canvas) {
            this.canvas.remove();
            this.canvas = undefined;
            this.ctx = undefined;
        }
    }
    /**
     * Polls the memory API and updates internal state.
     */
    update() {
        const mem = performance.memory;
        // use current committed heap as the denominator
        const usedRatio = mem.usedJSHeapSize / mem.totalJSHeapSize;
        let ratio = Math.min(1, Math.max(0, usedRatio));
        ratio = ratio / 2;
        const isGC = mem.usedJSHeapSize < this.lastUsedHeap;
        this.lastUsedHeap = mem.usedJSHeapSize;
        this.data.push(ratio);
        this.gcHistory.push(isGC);
        if (this.data.length > this.maxDataPoints) {
            this.data.shift();
            this.gcHistory.shift();
        }
        this.render(mem);
    }
    render(mem) {
        const { ctx, canvasWidth, canvasHeight, data, gcHistory } = this;
        if (!ctx) {
            return;
        }
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
        // draw bars
        data.forEach((v, i) => {
            const barH = v * canvasHeight;
            ctx.fillStyle = 'lightgreen';
            ctx.fillRect(i, canvasHeight - barH, 1, barH);
            // if a GC happened at this sample, overlay a thin red stripe
            if (gcHistory[i]) {
                ctx.fillStyle = 'red';
                ctx.fillRect(i, 0, 1, canvasHeight);
            }
        });
        // draw text
        ctx.fillStyle = 'white';
        ctx.font = '10px monospace';
        const usedMB = (mem.usedJSHeapSize / 1024 / 1024).toFixed(2);
        const totalMB = (mem.totalJSHeapSize / 1024 / 1024).toFixed(2);
        ctx.fillText(`${usedMB}MB / ${totalMB}MB`, 5, 12);
    }
    template() {
        this.canvas = document.createElement('canvas');
        this.canvas.width = this.canvasWidth;
        this.canvas.height = this.canvasHeight;
        this.rootElement.appendChild(this.canvas);
        return '';
    }
}
MemoryMonitor.VERSION = '$Revision: 125028 $';
